import React, { useCallback, useEffect, useState } from "react";
import { withStyles, Button, Grid, Divider } from "@material-ui/core";
import CustomTextField from "../../../../../../Dispatch/RouteExecution/DriverPopUp/common/CustomTextField";
import { finishDrawing, getFormattedDropYardData } from "../../utils";
import Events from "../../../../../../../../utilities/EventEmitter";
import drawConstant from "../../../../../common/DrawTools/DrawConstant";

const styles = (theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  sectionTitle: {
    fontSize: theme.textFontSize,
    color: theme.gridTextColor,
    marginTop: 10,
  },
  actionContainer: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  divider: {
    backgroundColor: theme.AppBGColor,
    marginTop: 8,
  },
  saveButton: {
    minWidth: 120,
  },
  circularProgress: {
    color: theme.white,
  },
});

const DropYardEditCard = ({
  dropYard,
  classes,
  disableEdit,
  isNewDropYard,
  handleSaveRegion,
  initialBoundary,
  resetInitialBoundary,
}) => {
  const [formData, setFormData] = useState({});

  const handleRegionUpdate = useCallback(
    (feature) => {
      // This event handler is only used to update the boundary value here.
      // For checking if the drawn polygon is valid or not and handle it, the event handler in DropYardsDrawer is used

      // We can update the boundary only if the feature is a new drop yard or
      // if the feature id matches the formData id in case of updating region
      if (isNewDropYard || feature?.id === formData?.id) {
        const boundary = feature?.geometry?.coordinates?.[0];
        if (boundary) {
          setFormData((prevState) => ({
            ...prevState,
            boundary,
          }));
        }
      }
    },
    [formData, setFormData]
  );

  useEffect(() => {
    const data = getFormattedDropYardData(dropYard);
    if (initialBoundary) {
      data.boundary = initialBoundary;
      resetInitialBoundary();
    }
    setFormData(data);
  }, [dropYard]);

  useEffect(() => {
    Events.on(
      drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_UPDATED,
      handleRegionUpdate
    );
    return () => {
      Events.off(
        drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_UPDATED,
        handleRegionUpdate
      );
    };
  }, [handleRegionUpdate]);

  const handleInventoryChange = (equipmentType, equipmentId) => (count) => {
    const equipmentList = formData[equipmentType]?.map((item) => {
      if (item.id === equipmentId) {
        return { ...item, count };
      }
      return item;
    });
    setFormData((prevState) => ({
      ...prevState,
      [equipmentType]: equipmentList,
    }));
  };

  const renderEquipmentInputs = ({ title, items, equipmentType }) => (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>{title}</div>
      <Grid container spacing={8}>
        {items?.map((item) => (
          <Grid item xs={6} key={item.id}>
            <CustomTextField
              id={item.id}
              label={item.name}
              value={item.count}
              handleChange={handleInventoryChange(equipmentType, item.id)}
              type="number"
              isDecimalAllowed={false}
              hideIncrementer={true}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );

  const handleCancel = () => {
    disableEdit();
    finishDrawing(); //Reset the drawing tools for further drawing
  };

  return (
    <div className={classes.container}>
      <Grid container className={classes.section}>
        <Grid item xs={6}>
          <CustomTextField
            key={formData?.id} // To force re-render when code changes, otherwise value will overlap with label
            id="DropYardCode"
            label="Drop Yard Code"
            value={formData?.code}
            disabled={true}
          />
        </Grid>
      </Grid>

      {renderEquipmentInputs({
        title: "TRAILER DETAILS",
        items: formData?.trailers,
        equipmentType: "trailers",
      })}
      <Divider className={classes.divider} />
      {renderEquipmentInputs({
        title: "TRACTOR DETAILS",
        items: formData?.tractors,
        equipmentType: "tractors",
      })}

      <div className={classes.actionContainer}>
        <Button color="primary" variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => handleSaveRegion(formData)}
          className={classes.saveButton}
        >
          {isNewDropYard ? "Add Region" : "Update Region"}
        </Button>
      </div>
    </div>
  );
};

export default withStyles(styles)(DropYardEditCard);
